import * as React from 'react';
import { useSelector } from 'react-redux';

import { EUserCardAvatarType } from '../components/UserCard/UserCardAvatar';
import { ACCOUNT_TYPE_NAME } from '../constants/accountTypeName';
import { CHATBOT_ID } from '../constants/chatbotId';
import { chatListSelector, currentOpenChatSelector, userIdSelector } from '../selectors';
import { IUserInChatListSchema } from '../types/userData';
import { formatPhoneNumber } from '../utils/formatPhoneNumber';

export type TFormattedCompanionData = {
  companionId: number | undefined;
  companionName: string | undefined;
  companionIdStr: string | undefined;
  accountTypeName: string;
  imageType: EUserCardAvatarType;
  formatPhone: string | undefined;
  avatarUrl: string | undefined;
  profileLink: string | undefined;
};

export const useCompanionData = (chatId?: string): TFormattedCompanionData => {
  const userId = useSelector(userIdSelector);
  const { chats } = useSelector(chatListSelector);
  const {
    chatId: currentOpenChatId,
    frameCompanion,
    users: currentOpenChatUsers,
  } = useSelector(currentOpenChatSelector) || {};

  const chatUsers = React.useMemo(() => {
    if (chatId === currentOpenChatId) {
      return (currentOpenChatUsers as IUserInChatListSchema[]) || [];
    }

    const chat = chats?.find(chat => chat.chatId === chatId);

    return (chat?.users as IUserInChatListSchema[]) || [];
  }, [chatId, chats, currentOpenChatId, currentOpenChatUsers]);

  const companion = React.useMemo(() => {
    const opponent = chatUsers.find(user => user.chatRole === 'opponent');
    const initiator = chatUsers.find(user => user.chatRole === 'initiator');
    const companion = chatUsers.find(user => user.chatRole === 'companion');

    if (chatId === CHATBOT_ID) {
      return frameCompanion;
    }

    // Если зашли за инициатора - показываем оппонента, если его нет - компаньона
    if (initiator?.userId === userId) {
      return opponent || companion;
    }

    // Если зашли за оппонента - показываем инициатора
    if (opponent?.userId === userId) {
      return initiator;
    }

    // Если чат iframe
    if (currentOpenChatId === chatId && frameCompanion) {
      return frameCompanion;
    }

    // Если зашли за другого участника - показываем инициатора
    return initiator;
  }, [chatId, chatUsers, currentOpenChatId, frameCompanion, userId]);

  const {
    name,
    agencyName,
    agentAccountType,
    userId: companionId,
    phones,
    agentAvatarUrl: avatarUrl,
    urlToAgentPublicProfile: profileLink,
  } = companion || {};

  const companionName = React.useMemo(() => {
    return name || agencyName;
  }, [name, agencyName]);

  const companionIdStr = React.useMemo(() => {
    return companionId ? `id ${companionId}` : undefined;
  }, [companionId]);

  const accountTypeName = React.useMemo(() => {
    return agentAccountType ? ACCOUNT_TYPE_NAME[agentAccountType] : 'Пользователь';
  }, [agentAccountType]);

  const imageType = React.useMemo(() => {
    return agentAccountType &&
      ['agency', 'management_company', 'rent_department', 'specialist', 'managementCompany', 'rentDepartment'].includes(
        agentAccountType,
      )
      ? EUserCardAvatarType.Agent
      : EUserCardAvatarType.User;
  }, [agentAccountType]);

  const formatPhone = React.useMemo(() => {
    return phones?.length ? formatPhoneNumber(phones[0]) : undefined;
  }, [phones]);

  return {
    companionId,
    companionName,
    companionIdStr,
    accountTypeName,
    imageType,
    formatPhone,
    avatarUrl,
    profileLink,
  };
};
